import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Grid2,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import axios from "axios";
import logoIcon from '../../assets/images/marca_icon.svg';
import { ROUTES } from '../single-page/constants/routes';
import ContactUsModal from './ContactUsModal';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useModals } from '../hooks/useModals';
import { useUser } from '../hooks/useUser';
import { brandTokens } from '../single-page/theme';

const MENU_ITEMS = [
  { label: 'Consultas', to: 'plateSearch', isScrollLink: true },
  { label: 'Como Funciona', to: 'howItWorks', isScrollLink: true },
  { label: 'Planos', to: 'meetOurInspections', isScrollLink: true },
  { label: 'Sobre Nós', to: 'aboutUs', isScrollLink: true },
  { label: 'Fale Conosco', to: 'contact', isScrollLink: true, action: 'contact' },
];

const StyledHeader = styled(AppBar)(({ theme }) => ({
  background: 'rgba(1, 47, 71, 0.85)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: 'none',
  position: 'sticky',
  top: 0,
  zIndex: 1100,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  padding: theme.spacing(1.5, 0),
  minHeight: '80px',
  transition: 'all 0.3s ease',
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 0),
    minHeight: '64px',
  },
}));

const ConsultCtaButton = styled(Button)(({ theme }) => ({
  background: brandTokens.signatureGradient,
  color: '#FFFFFF',
  fontWeight: 600,
  fontSize: '14px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: theme.spacing(1.25, 2.5),
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(81, 223, 148, 0.2)',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: brandTokens.signatureGradient,
    filter: 'brightness(1.08)',
    boxShadow: '0 8px 28px rgba(81, 223, 148, 0.28)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const LogoContainer = styled(Grid2)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: '100%',
  
  '& img': {
    height: '40px',
    width: 'auto',
    
    [theme.breakpoints.down('sm')]: {
      height: '32px',
    },
  },
  
  [theme.breakpoints.up("xl")]: {
    marginLeft: theme.spacing(8),
  },
  [theme.breakpoints.down("lg")]: {
    marginLeft: theme.spacing(4),
  },
  [theme.breakpoints.down("md")]: {
    marginLeft: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    marginLeft: theme.spacing(3),
  },
}));

const MenuContainer = styled(Grid2)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  height: '100%',
}));

const DesktopMenu = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(3),
  height: '100%',
  
  "& a": {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: brandTokens.onSurfaceVariant,
    textDecoration: "none",
    position: "relative",
    padding: theme.spacing(1),
    whiteSpace: 'nowrap',
    transition: "color 0.2s ease",
    
    "&:hover": {
      color: brandTokens.cyan,
      
      "&::after": {
        width: "100%",
      },
    },
    
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: 0,
      height: "2px",
      backgroundColor: brandTokens.green,
      transition: "width 0.2s ease",
    },
  },
}));

const MenuOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(1, 47, 71, 0.85)',
  backdropFilter: 'blur(8px)',
  zIndex: theme.zIndex.modal,
  opacity: 0,
  visibility: 'hidden',
  transition: 'all 0.3s ease-in-out',
  
  '&.open': {
    opacity: 1,
    visibility: 'visible',
  },
}));

const FullScreenMenu = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: theme.zIndex.modal + 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(4, 2),
  transform: 'translateY(-100%)',
  transition: 'transform 0.3s ease-in-out',
  background: 'rgba(1, 47, 71, 0.85)',
  backdropFilter: 'blur(8px)',
  
  '&.open': {
    transform: 'translateY(0)',
  },
}));

const MobileMenuItem = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  color: '#fff',
  fontSize: '20px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  
  '& a': {
    color: '#fff',
    textDecoration: 'none',
    display: 'block',
    width: '100%',
  },

  '& .MuiSvgIcon-root': {
    verticalAlign: 'middle',
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  color: '#fff',
  fontSize: '24px',
  padding: theme.spacing(1.5),
  minWidth: '48px',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'rotate(90deg)',
  },
}));

const MenuLink = ({ item, onClick, currentUser, setIsModalOpen, setContactOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    
    if (item.action === 'contact') {
      setContactOpen(true);
      return;
    }

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(item.to);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(item.to);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    onClick?.();
  };

  if (item.isScrollLink) {
    return (
      <Box
        component="a"
        href={`/#${item.to}`}
        onClick={handleClick}
        sx={{
          color: 'inherit',
          textDecoration: 'none',
          cursor: 'pointer'
        }}
      >
        {item.label}
      </Box>
    );
  }

  return (
    <Link to={item.to} onClick={handleClick}>
      {item.label}
    </Link>
  );
};

const scrollToSection = (sectionId) => {
  const go = () => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (window.location.pathname !== '/') {
    window.location.assign(`/#${sectionId}`);
    return;
  }
  go();
};

const DesktopMenuItems = ({ currentUser, setIsModalOpen, setContactOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleConsultClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToSection('plateSearch'), 100);
      return;
    }
    scrollToSection('plateSearch');
  };

  return (
    <DesktopMenu>
      {MENU_ITEMS.map((item) => (
        <Box 
          key={item.label}
          component="div"
        >
          <MenuLink 
            item={item}
            currentUser={currentUser}
            setIsModalOpen={setIsModalOpen}
            setContactOpen={setContactOpen}
          />
        </Box>
      ))}
      <Box component="div">
        <UserStatus 
          currentUser={currentUser} 
          onLoginClick={() => setIsModalOpen(true)}
        />
      </Box>
      <ConsultCtaButton onClick={handleConsultClick} disableElevation>
        Consultar Placa
      </ConsultCtaButton>
    </DesktopMenu>
  );
};

const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      setLoading(true);
      await axios.delete(ROUTES.SIGN_OUT);
      window.location = ROUTES.HOME;
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    } finally {
      setLoading(false);
    }
  };

  return { loading, handleLogout, navigate };
};

const UserStatus = ({ currentUser, onLoginClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { loading, handleLogout, navigate } = useAuth();
  const location = useLocation();

  const handleClose = () => setAnchorEl(null);
  const handleNavigation = (path) => {
    handleClose();
    navigate(path);
  };

  if (!currentUser?.id) {
    return (
      <Box 
        component="div"
        sx={{
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': {
            color: brandTokens.cyan,
          }
        }}
        onClick={onLoginClick}
      >
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: '0.04em',
          }}
        >
          Entrar
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="div"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': {
            color: brandTokens.cyan,
          }
        }}
      >
        <Avatar 
          alt={currentUser.name} 
          sx={{ 
            width: 32, 
            height: 32,
            bgcolor: brandTokens.blueAccent,
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.1)'
            }
          }}
        >
          {currentUser.name?.charAt(0).toUpperCase()}
        </Avatar>
        <Typography 
          component="span"
          sx={{ display: 'inline' }}
        >
          {currentUser.name}
        </Typography>
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: '200px',
            borderRadius: '8px',
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              py: 1.5,
              px: 2,
              gap: 1.5,
              color: 'text.secondary',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(35, 156, 196, 0.08)',
                color: '#239CC4'
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={() => handleNavigation(ROUTES.ACCOUNT)}
          sx={{ 
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: location.pathname === ROUTES.ACCOUNT ? 'rgba(35, 156, 196, 0.08)' : 'inherit',
            color: location.pathname === ROUTES.ACCOUNT ? '#239CC4 !important' : 'inherit',
            '&:hover': {
              backgroundColor: location.pathname === ROUTES.ACCOUNT 
                ? 'rgba(35, 156, 196, 0.12)' 
                : 'rgba(35, 156, 196, 0.08)'
            }
          }}
        >
          <AccountCircleIcon color={location.pathname === ROUTES.ACCOUNT ? 'primary' : 'inherit'} />
          <Typography color="inherit">Minha Conta</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={() => handleNavigation(ROUTES.MY_INSPECTIONS)}
          sx={{ 
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: location.pathname === ROUTES.MY_INSPECTIONS ? 'rgba(35, 156, 196, 0.08)' : 'inherit',
            color: location.pathname === ROUTES.MY_INSPECTIONS ? '#239CC4 !important' : 'inherit',
            '&:hover': {
              backgroundColor: location.pathname === ROUTES.MY_INSPECTIONS 
                ? 'rgba(35, 156, 196, 0.12)' 
                : 'rgba(35, 156, 196, 0.08)'
            }
          }}
        >
          <VisibilityIcon color={location.pathname === ROUTES.MY_INSPECTIONS ? 'primary' : 'inherit'} />
          <Typography color="inherit">Minhas Vistorias</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={handleLogout}
          disabled={loading}
          sx={{
            color: '#d32f2f !important',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08) !important',
            }
          }}
        >
          <LogoutIcon sx={{ color: 'inherit' }} />
          <Typography sx={{ color: 'inherit' }}>
            {loading ? 'Saindo...' : 'Sair'}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

const MobileMenu = ({ currentUser, setIsModalOpen, setContactOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleClose = () => setIsOpen(false);
  const handleNavigation = (path) => {
    handleClose();
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await axios.delete(ROUTES.SIGN_OUT);
      handleClose();
      window.location = ROUTES.HOME;
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button 
        onClick={() => setIsOpen(true)}
        sx={{ padding: 1, minWidth: 'unset' }}
      >
        <MenuIcon sx={{ color: "#fff", fontSize: 32 }} />
      </Button>
      
      {isOpen && (
        <>
          <MenuOverlay className="open" onClick={handleClose} />
          <FullScreenMenu className="open">
            <CloseButton onClick={handleClose}>✕</CloseButton>
            
            <Box sx={{ 
              mt: 8, 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}>
              <Box sx={{ 
                width: '100%',
                mb: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                {!currentUser?.id ? (
                  <Button
                    onClick={() => {
                      handleClose();
                      setIsModalOpen(true);
                    }}
                    sx={{
                      color: '#fff',
                      backgroundColor: 'rgba(35, 156, 196, 0.2)',
                      borderRadius: '50px',
                      padding: '10px 30px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(35, 156, 196, 0.3)',
                      }
                    }}
                  >
                    <AccountCircleIcon />
                    <Typography>Fazer Login</Typography>
                  </Button>
                ) : (
                  <Box sx={{
                    width: '100%',
                    maxWidth: '300px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <Avatar 
                      alt={currentUser.name} 
                      sx={{ 
                        width: 56, 
                        height: 56,
                        bgcolor: '#239CC4',
                        margin: '0 auto 12px'
                      }}
                    >
                      {currentUser.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: '1.1rem',
                        color: '#fff',
                        mb: 1
                      }}
                    >
                      {currentUser.name}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ mb: 4 }}>
                {MENU_ITEMS.map((item) => (
                  <MobileMenuItem 
                    key={item.label}
                    onClick={() => {
                      handleClose();
                      if (item.requiresAuth && !currentUser?.id) {
                        setIsModalOpen(true);
                      }
                    }}
                  >
                    <MenuLink 
                      item={item}
                      currentUser={currentUser}
                      setIsModalOpen={setIsModalOpen}
                      setContactOpen={setContactOpen}
                    />
                  </MobileMenuItem>
                ))}
              </Box>

              {currentUser?.id && (
                <Box sx={{
                  mt: 'auto',
                  pt: 3,
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <Button
                    onClick={() => handleNavigation(ROUTES.ACCOUNT)}
                    fullWidth
                    sx={{
                      color: '#fff',
                      justifyContent: 'flex-start',
                      padding: '12px',
                      gap: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                    <AccountCircleIcon />
                    <Typography>Minha Conta</Typography>
                  </Button>

                  <Button
                    onClick={() => handleNavigation(ROUTES.MY_INSPECTIONS)}
                    fullWidth
                    sx={{
                      color: '#fff',
                      justifyContent: 'flex-start',
                      padding: '12px',
                      gap: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                    <VisibilityIcon />
                    <Typography>Minhas Vistorias</Typography>
                  </Button>

                  <Button
                    onClick={handleLogout}
                    fullWidth
                    disabled={loading}
                    sx={{
                      color: '#ff4444',
                      justifyContent: 'flex-start',
                      padding: '12px',
                      gap: 2,
                      mt: 2,
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 99, 99, 0.1)'
                      }
                    }}
                  >
                    <LogoutIcon />
                    <Typography>
                      {loading ? 'Saindo...' : 'Sair'}
                    </Typography>
                  </Button>
                </Box>
              )}
            </Box>
          </FullScreenMenu>
        </>
      )}
    </>
  );
};

const Header = ({ onLoginClick, onRegisterClick }) => {
  const { currentUser } = useUser();
  const {
    handleOpenLogin,
    handleOpenRegister,
    contactOpen,
    setContactOpen
  } = useModals();
  
  return (
    <>
      <StyledHeader position="sticky">
        <Container maxWidth="xl">
          <StyledToolbar>
            <Grid2 container spacing={2} sx={{ alignItems: "center", flexWrap: "nowrap" }}>
              <LogoContainer size="auto">
                <Link to={ROUTES.HOME}>
                  <img src={logoIcon} alt="Logo" />
                </Link>
              </LogoContainer>

              <MenuContainer size="grow">
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <DesktopMenuItems 
                    currentUser={currentUser}
                    setIsModalOpen={handleOpenLogin}
                    setContactOpen={setContactOpen}
                  />
                </Box>
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <MobileMenu 
                    currentUser={currentUser}
                    setIsModalOpen={handleOpenLogin}
                    setContactOpen={setContactOpen}
                  />
                </Box>
              </MenuContainer>
            </Grid2>
          </StyledToolbar>
        </Container>
      </StyledHeader>

      <ContactUsModal 
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </>
  );
};

export default Header;
